import { useEffect, useRef } from "react";

const HOLDER_SELECTOR = ".rc-virtual-list-holder";
const INNER_SELECTOR = ".rc-virtual-list-holder-inner";
const MOVEMENT_EPSILON = 0.5;

/**
 * antd's Select/Dropdown lists (rc-virtual-list) always render their scrollable
 * area with `overflow: hidden` and move the list content manually via JS
 * (transform) in response to touchmove, rather than relying on the browser's
 * native scrolling. When the list is swiped past its own top/bottom boundary,
 * rc-virtual-list intentionally does *not* call `preventDefault()` on that
 * touchmove so desktop mouse-wheel scrolling can bubble up to the page. On
 * mobile this causes the *page underneath* the dropdown to also scroll while
 * the user is still scrolling through the dropdown list, since the
 * touchmove's default action is left for the browser to handle and the
 * nearest scrollable ancestor (the page) ends up receiving it too early.
 *
 * This hook tracks each touch gesture inside an open dropdown list and only
 * lets the browser's default scroll behavior (page scroll) through once the
 * list has actually stopped moving in response to the drag - i.e. it has
 * reached its own top/bottom edge. While the list is still actively moving,
 * the touchmove is prevented from reaching the page, so only the dropdown
 * scrolls.
 *
 * We deliberately avoid trying to independently compute the list's
 * scrollTop/scrollHeight/clientHeight from its rendered geometry (parsing the
 * `translateY(px)` transform and comparing it against measured element
 * heights) - rc-virtual-list's own internal maxScrollHeight calculation does
 * not line up exactly with what can be measured from the outside (buffer
 * rows, item-height rounding, etc. leave a persistent few-pixel gap), so that
 * approach produced false negatives at the real boundary. Instead we use the
 * fact that once the list can no longer scroll further in a direction, its
 * own touchmove handler stops updating the transform - so if the transform
 * didn't change on the previous touchmove despite the user still dragging
 * the same direction, we know we're at the edge, and it's safe to let this
 * touchmove's default (page scroll) action proceed.
 */
function readTranslateY(holder) {
    const inner = holder.querySelector(INNER_SELECTOR);
    if (!inner) {
        return null;
    }

    const { transform } = window.getComputedStyle(inner);
    const match = transform && transform !== "none" ? transform.match(/matrix\(([^)]+)\)/) : null;
    if (!match) {
        return 0;
    }

    const values = match[1].split(",").map((value) => parseFloat(value.trim()));
    return values[5] || 0;
}

function usePreventDropdownScrollChaining() {
    const gestureRef = useRef(null);

    useEffect(() => {
        const onTouchStart = (event) => {
            const holder = event.target?.closest?.(HOLDER_SELECTOR);
            if (holder && event.touches?.length === 1) {
                gestureRef.current = {
                    holder,
                    lastY: event.touches[0].clientY,
                    lastTranslateY: readTranslateY(holder),
                    lastDirection: null,
                    moveCount: 0
                };
            } else {
                gestureRef.current = null;
            }
        };

        const onTouchMove = (event) => {
            const gesture = gestureRef.current;
            if (!gesture || !event.touches?.length) {
                return;
            }

            const currentY = event.touches[0].clientY;
            const draggingDown = gesture.lastY - currentY < 0; // finger moving down the screen
            gesture.lastY = currentY;

            const translateY = readTranslateY(gesture.holder);
            if (translateY === null) {
                return;
            }

            // If the drag direction just changed, or this is the very first move
            // of the gesture, we don't yet know whether the list can move
            // further that way (the transform hasn't had a chance to update in
            // response to this gesture yet) - default to blocking page scroll
            // for this one move so we don't accidentally let a legitimate
            // in-list scroll leak through to the page.
            const directionChanged = gesture.lastDirection !== null && gesture.lastDirection !== draggingDown;
            const movedSinceLastFrame = Math.abs(translateY - gesture.lastTranslateY) > MOVEMENT_EPSILON;
            const isFirstMove = gesture.moveCount === 0;

            gesture.lastTranslateY = translateY;
            gesture.lastDirection = draggingDown;
            gesture.moveCount += 1;

            if (isFirstMove || directionChanged || movedSinceLastFrame) {
                event.preventDefault();
            }
            // else: the list didn't move on the previous touchmove even though
            // we kept dragging the same direction, so it's already at its edge -
            // let this touchmove's default action (page scroll) go through.
        };

        const onTouchEnd = () => {
            gestureRef.current = null;
        };

        document.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
        document.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
        document.addEventListener("touchend", onTouchEnd, { passive: true, capture: true });
        document.addEventListener("touchcancel", onTouchEnd, { passive: true, capture: true });

        return () => {
            document.removeEventListener("touchstart", onTouchStart, { capture: true });
            document.removeEventListener("touchmove", onTouchMove, { capture: true });
            document.removeEventListener("touchend", onTouchEnd, { capture: true });
            document.removeEventListener("touchcancel", onTouchEnd, { capture: true });
        };
    }, []);
}

export default usePreventDropdownScrollChaining;
